import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ArrowLeft, GraduationCap, UserCheck, Plus, 
  Search, X, Filter, Calendar, Clock, ChevronLeft, ChevronRight, ChevronDown, Check
} from 'lucide-react';
import api from '../../connector/api';
import NewViolationModal from '../components/NewViolationModal';

const STANDARD_VIOLATIONS = [
  "No ID Card",
  "No Tie",
  "No PE Uniform",
  "Wrong Shoes",
  "Wrong Hair Color"
];

const renderSafeString = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    return val.name || val.term_name || val.title || val.term || '';
  }
  return String(val);
};

function formatTime(timeStr, dateStr) {
  if (!timeStr && !dateStr) return '—';
  if (timeStr && (timeStr.includes('AM') || timeStr.includes('PM'))) {
    return timeStr;
  }
  try {
    let dateObj;
    if (timeStr && timeStr.includes(':')) {
      const today = dateStr || '2026-01-01';
      dateObj = new Date(`${today}T${timeStr}`);
    } else if (dateStr) {
      dateObj = new Date(dateStr);
    }

    if (dateObj && !isNaN(dateObj.getTime())) {
      return dateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  } catch (e) {
    // Fallback on parse failure
  }
  return renderSafeString(timeStr) || '—';
}

function DetailSkeleton() {
  return (
    <div className="p-6 max-w-[1400px] w-full mx-auto space-y-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-8 w-32 bg-slate-200 rounded-xl"></div>
        <div className="h-8 w-32 bg-slate-200 rounded-xl"></div>
      </div>
      <div className="bg-white border border-slate-200/80 rounded-lg p-5 shadow-xs space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-slate-200 shrink-0"></div>
          <div className="space-y-2">
            <div className="h-5 w-40 bg-slate-200 rounded-lg"></div>
            <div className="h-3 w-60 bg-slate-100 rounded-md"></div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200/80 rounded-lg shadow-xs overflow-hidden p-5 space-y-4">
        <div className="h-8 w-full bg-slate-100 rounded-xl"></div>
        <div className="space-y-2 pt-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 w-full bg-slate-50 rounded-lg border border-slate-100"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function StudentUniformDetail({ studentId, currentUser, onBack, onOpenModal }) {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Filtering & Search States
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('ALL');
  const [selectedTerm, setSelectedTerm] = useState('ALL');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedViolationFilter, setSelectedViolationFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Custom Dropdowns UI States
  const [isViolationDropdownOpen, setIsViolationDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  const [dropdownSearch, setDropdownSearch] = useState('');
  const [yearDropdownSearch, setYearDropdownSearch] = useState('');

  const violationDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);

  const fetchStudentDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/uniform-infraction/${studentId}/`);
      const data = response.data;

      setStudentInfo({
        id: renderSafeString(data.student_id || studentId),
        name: renderSafeString(data.student_name) || "Student Detail",
        gradeSection: renderSafeString(data.grade_section) || "N/A",
        homeroomTeacher: renderSafeString(data.homeroom_teacher) || "N/A",
      });

      setHistoryLogs(data.logs || []);
    } catch (error) {
      console.error("Error fetching student infraction details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) fetchStudentDetails();
  }, [studentId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSchoolYear, selectedTerm, selectedDate, selectedViolationFilter, searchTerm]);

  const handleSaveInfraction = async (payload, infractionId) => {
    try {
      if (infractionId) {
        await api.put(`/uniform-infraction/${infractionId}/`, payload);
      } else {
        await api.post('/uniform-infraction/', payload);
      }
      
      await fetchStudentDetails();
      setIsModalOpen(false);
      return true; 
    } catch (error) {
      console.error("Error committing infraction modification:", error);
      throw error; 
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (violationDropdownRef.current && !violationDropdownRef.current.contains(event.target)) {
        setIsViolationDropdownOpen(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setIsYearDropdownOpen(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setIsDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const availableSchoolYears = useMemo(() => {
    const years = new Set();
    historyLogs.forEach(log => {
      let year = renderSafeString(log.school_year || log.academic_year || log.year);
      if (!year && log.school_year_term) {
        year = renderSafeString(log.school_year_term.year || log.school_year_term.school_year || log.school_year_term.name);
      }
      if (!year && log.date_of_infraction) {
        const parsedYear = new Date(log.date_of_infraction).getFullYear();
        if (!isNaN(parsedYear)) {
          year = `${parsedYear}-${parsedYear + 1}`;
        }
      }
      if (year) years.add(String(year));
    });
    return Array.from(years);
  }, [historyLogs]);

  const availableTerms = useMemo(() => {
    if (selectedSchoolYear === 'ALL') return [];
    
    const terms = new Set();
    historyLogs.forEach(log => {
      let logYear = renderSafeString(log.school_year || log.academic_year || log.year || log.school_year_term?.year);
      if (!logYear && log.date_of_infraction) {
        const parsedYear = new Date(log.date_of_infraction).getFullYear();
        if (!isNaN(parsedYear)) logYear = `${parsedYear}-${parsedYear + 1}`;
      }

      const term = renderSafeString(log.term || log.school_year_term?.term);

      if (logYear === selectedSchoolYear && term) {
        terms.add(String(term));
      }
    });

    return Array.from(terms);
  }, [historyLogs, selectedSchoolYear]);

  const filteredSchoolYears = useMemo(() => {
    return availableSchoolYears.filter(year => 
      year.toLowerCase().includes(yearDropdownSearch.toLowerCase().trim())
    );
  }, [availableSchoolYears, yearDropdownSearch]);

  const violationCounts = useMemo(() => {
    const counts = {};
    if (typeof STANDARD_VIOLATIONS !== 'undefined') {
      STANDARD_VIOLATIONS.forEach(type => { counts[type] = 0; });
    }

    historyLogs.forEach(log => {
      const item = renderSafeString(log.specific_item || log.violation_type) || "Other";
      const match = typeof STANDARD_VIOLATIONS !== 'undefined' 
        ? STANDARD_VIOLATIONS.find(v => v.toLowerCase() === item.toLowerCase()) 
        : null;
      const key = match || item;
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([type, count]) => ({ type, count }));
  }, [historyLogs]);

  const filteredLogs = useMemo(() => {
    return historyLogs.filter((log) => {
      let logYear = renderSafeString(log.school_year || log.academic_year || log.year || log.school_year_term?.year);
      if (!logYear && log.date_of_infraction) {
        const parsedYear = new Date(log.date_of_infraction).getFullYear();
        if (!isNaN(parsedYear)) logYear = `${parsedYear}-${parsedYear + 1}`;
      }

      const logTerm = renderSafeString(log.term || log.school_year_term?.term);
      const logDate = renderSafeString(log.date_of_infraction);
      const logViolation = renderSafeString(log.specific_item || log.violation_type).toLowerCase();
      const logNotes = renderSafeString(log.detailed_note || log.notes).toLowerCase();
      const loggedBy = renderSafeString(log.logged_by_name || log.logged_by?.full_name).toLowerCase();
      const query = searchTerm.toLowerCase().trim();

      const matchesYear = selectedSchoolYear === 'ALL' || logYear === selectedSchoolYear;
      const matchesTerm = selectedTerm === 'ALL' || logTerm === selectedTerm;
      const matchesDate = !selectedDate || logDate === selectedDate;
      const matchesCategory = selectedViolationFilter === 'ALL' || logViolation === selectedViolationFilter.toLowerCase();

      const matchesSearch = 
        !query || 
        logViolation.includes(query) || 
        logNotes.includes(query) ||
        loggedBy.includes(query);

      return matchesYear && matchesTerm && matchesDate && matchesCategory && matchesSearch;
    });
  }, [historyLogs, selectedSchoolYear, selectedTerm, selectedDate, selectedViolationFilter, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unscheduled / Unknown Date";
    const parsedDate = new Date(dateStr);
    if (isNaN(parsedDate.getTime())) return dateStr;

    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const groupedLogs = useMemo(() => {
    const groups = {};

    const sorted = [...filteredLogs].sort((a, b) => {
      const dateA = renderSafeString(a.date_of_infraction);
      const dateB = renderSafeString(b.date_of_infraction);
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      
      const timeA = renderSafeString(a.time_of_infraction || a.created_at);
      const timeB = renderSafeString(b.time_of_infraction || b.created_at);
      return timeA.localeCompare(timeB);
    });

    sorted.forEach((log) => {
      const rawDate = renderSafeString(log.date_of_infraction);
      const dateKey = formatDate(rawDate); 
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(log);
    });

    return Object.entries(groups);
  }, [filteredLogs]);

  const totalPages = Math.ceil(groupedLogs.length / itemsPerPage) || 1;
  const paginatedGroupedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return groupedLogs.slice(startIndex, startIndex + itemsPerPage);
  }, [groupedLogs, currentPage, itemsPerPage]);

  const filteredDropdownOptions = useMemo(() => {
    return violationCounts.filter(item => 
      item.type.toLowerCase().includes(dropdownSearch.toLowerCase().trim())
    );
  }, [violationCounts, dropdownSearch]);

  const clearAllFilters = () => {
    setSelectedSchoolYear('ALL');
    setSelectedTerm('ALL');
    setSelectedDate('');
    setSelectedViolationFilter('ALL');
    setSearchTerm('');
  };

  const getInterventionBadge = (type) => {
    const key = renderSafeString(type).toLowerCase();
    switch (key) {
      case 'telegram':
        return <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-blue-50 text-blue-700 border border-blue-200/60 inline-flex items-center gap-1">Telegram Notice</span>;
      case 'call':
        return <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-purple-50 text-purple-700 border border-purple-200/60 inline-flex items-center gap-1">Parent Call</span>;
      case 'detention':
        return <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-rose-50 text-rose-700 border border-rose-200/60 inline-flex items-center gap-1">Detention</span>;
      case 'verbal':
      default:
        return <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-700 border border-slate-200/60 inline-flex items-center gap-1">Verbal Warning</span>;
    }
  };

  const hasActiveFilters = 
    selectedSchoolYear !== 'ALL' || 
    selectedTerm !== 'ALL' || 
    selectedDate !== '' || 
    selectedViolationFilter !== 'ALL' || 
    searchTerm !== '';

  return (
      <div className="p-2 max-w-[1400px] w-full mx-auto space-y-4">
        
        {loading ? (
          <DetailSkeleton />
        ) : (
          <>
            {/* Action Bar & Profile Overview */}
            <div className="bg-white rounded-lg border border-slate-200/80 shadow-xs p-5 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <button
                    type="button"
                    onClick={onBack}
                    className="p-1.5 rounded-xl text-slate-500 hover:text-slate-900 bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                    title="Back to Directory"
                  >
                    <ArrowLeft size={16} />
                  </button>

                  <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-sm shadow-2xs shrink-0">
                    {studentInfo?.name ? studentInfo.name.split(' ').map(n => n[0]).join('') : 'ST'}
                  </div>

                  <div className="space-y-0.5">
                    <h1 className="text-sm font-bold text-slate-900 tracking-wider">
                      {studentInfo?.name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 font-medium">
                      <span className="flex items-center gap-1">
                        <GraduationCap size={13} className="text-slate-400" />
                        Class: <strong className="text-slate-700 font-semibold">{studentInfo?.gradeSection}</strong>
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="flex items-center gap-1">
                        <UserCheck size={13} className="text-slate-400" />
                        Homeroom: <strong className="text-slate-700 font-semibold">{studentInfo?.homeroomTeacher}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 text-xs font-mono font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-2xs cursor-pointer shrink-0"
                >
                  <Plus size={15} /> Log Infraction
                </button>
              </div>
            </div>

            {/* Main Infraction History Card */}
            <div className="bg-white rounded-lg border border-slate-200/80 shadow-xs overflow-hidden">
              
              {/* Header Toolbar */}
              <div className="p-4 sm:p-5 border-b border-slate-100 space-y-3">
                <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-bold text-slate-900 tracking-wider">Infraction Log History</h2>
                    <span className="text-[11px] font-mono font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md">
                      {filteredLogs.length} Records
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* School Year Filter */}
                    <div className="relative" ref={yearDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsYearDropdownOpen(!isYearDropdownOpen);
                          setIsViolationDropdownOpen(false);
                          setIsDateDropdownOpen(false);
                        }}
                        className={`flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-mono font-semibold transition-colors cursor-pointer ${
                          selectedSchoolYear !== 'ALL' ? 'text-blue-600 bg-blue-50/50 border-blue-200' : 'text-slate-700 hover:bg-slate-100/80'
                        }`}
                      >
                        <Calendar size={13} className="text-slate-400" />
                        <span>{selectedSchoolYear === 'ALL' ? 'All Academic Years' : selectedSchoolYear}</span>
                        <ChevronDown size={13} className="text-slate-400" />
                      </button>

                      {isYearDropdownOpen && (
                        <div className="absolute right-0 mt-1.5 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-1.5 space-y-1 overflow-hidden font-mono">
                          <input
                            type="text"
                            placeholder="Search year..."
                            value={yearDropdownSearch}
                            onChange={(e) => setYearDropdownSearch(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                          <div className="max-h-40 overflow-y-auto space-y-0.5">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSchoolYear('ALL');
                                setSelectedTerm('ALL');
                                setIsYearDropdownOpen(false);
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer transition ${
                                selectedSchoolYear === 'ALL' ? 'bg-blue-50 text-blue-600 font-bold' : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              All Academic Years
                            </button>
                            {filteredSchoolYears.map((year) => (
                              <button
                                key={year}
                                type="button"
                                onClick={() => {
                                  setSelectedSchoolYear(year);
                                  setSelectedTerm('ALL');
                                  setIsYearDropdownOpen(false);
                                }}
                                className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer transition ${
                                  selectedSchoolYear === year ? 'bg-blue-50 text-blue-600 font-bold' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Cascading Term Filter */}
                    {selectedSchoolYear !== 'ALL' && availableTerms.length > 0 && (
                      <select
                        value={selectedTerm}
                        onChange={(e) => setSelectedTerm(e.target.value)}
                        className="bg-blue-50/60 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-xl text-xs font-mono font-semibold focus:outline-none cursor-pointer"
                      >
                        <option value="ALL">All Terms ({selectedSchoolYear})</option>
                        {availableTerms.map((term) => (
                          <option key={term} value={term}>{term}</option>
                        ))}
                      </select>
                    )}

                    {/* Date Picker */}
                    <div className="relative" ref={dateDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsDateDropdownOpen(!isDateDropdownOpen);
                          setIsViolationDropdownOpen(false);
                          setIsYearDropdownOpen(false);
                        }}
                        className={`flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-mono font-semibold transition-colors cursor-pointer ${
                          selectedDate ? 'text-emerald-700 bg-emerald-50/60 border-emerald-200' : 'text-slate-700 hover:bg-slate-100/80'
                        }`}
                      >
                        <Calendar size={13} className="text-slate-400" />
                        <span>{selectedDate || 'Select Date'}</span>
                        {selectedDate ? (
                          <X size={13} className="hover:text-emerald-900" onClick={(e) => { e.stopPropagation(); setSelectedDate(''); }} />
                        ) : (
                          <ChevronDown size={13} className="text-slate-400" />
                        )}
                      </button>

                      {isDateDropdownOpen && (
                        <div className="absolute right-0 mt-1.5 p-3 bg-white border border-slate-200 rounded-xl shadow-lg z-20">
                          <label className="block text-[11px] font-bold text-slate-500 mb-1">Filter Date</label>
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => {
                              setSelectedDate(e.target.value);
                              setIsDateDropdownOpen(false);
                            }}
                            className="px-2.5 py-1 text-xs font-medium border border-slate-200 rounded-lg bg-slate-50 outline-none cursor-pointer"
                          />
                        </div>
                      )}
                    </div>

                    {/* Violation Type Dropdown */}
                    <div className="relative" ref={violationDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsViolationDropdownOpen(!isViolationDropdownOpen);
                          setIsYearDropdownOpen(false);
                          setIsDateDropdownOpen(false);
                        }}
                        className={`flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-mono font-semibold transition-colors cursor-pointer ${
                          selectedViolationFilter !== 'ALL' ? 'text-red-600 bg-red-50/50 border-red-200' : 'text-slate-700 hover:bg-slate-100/80'
                        }`}
                      >
                        <Filter size={13} className="text-slate-400" />
                        <span>{selectedViolationFilter === 'ALL' ? 'All Violations' : selectedViolationFilter}</span>
                        <ChevronDown size={13} className="text-slate-400" />
                      </button>

                      {isViolationDropdownOpen && (
                        <div className="absolute right-0 mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-1.5 space-y-1 font-mono">
                          <input
                            type="text"
                            placeholder="Filter types..."
                            value={dropdownSearch}
                            onChange={(e) => setDropdownSearch(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                          <div className="max-h-44 overflow-y-auto space-y-0.5">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedViolationFilter('ALL');
                                setIsViolationDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium transition cursor-pointer ${
                                selectedViolationFilter === 'ALL' ? 'bg-red-50 text-red-600 font-bold' : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <span>All Violations</span>
                              <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-100 font-bold text-slate-600">{historyLogs.length}</span>
                            </button>

                            {filteredDropdownOptions.map((item, idx) => {
                              const isSelected = selectedViolationFilter.toLowerCase() === item.type.toLowerCase();
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => {
                                    setSelectedViolationFilter(item.type);
                                    setIsViolationDropdownOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium transition cursor-pointer ${
                                    isSelected ? 'bg-red-50 text-red-600 font-bold' : 'text-slate-700 hover:bg-slate-50'
                                  }`}
                                >
                                  <span className="truncate pr-2">{item.type}</span>
                                  <span className="text-[10px] px-1.5 py-0.2 rounded font-bold bg-slate-100 text-slate-500">{item.count}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Search Field */}
                    <div className="relative w-full sm:w-48">
                      <Search className="absolute left-3 top-2 text-slate-400" size={15} />
                      <input
                        type="text"
                        placeholder="Search notes/staff..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-7 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      {searchTerm && (
                        <button type="button" onClick={() => setSearchTerm('')} className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600">
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Active Filter Badges */}
                {hasActiveFilters && (
                  <div className="pt-2 flex flex-wrap items-center gap-1.5 text-xs font-mono">
                    <span className="text-slate-400 font-semibold text-[11px]">Filtered:</span>
                    {selectedSchoolYear !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                        Year: {selectedSchoolYear}
                        <X size={12} className="cursor-pointer hover:text-blue-900" onClick={() => { setSelectedSchoolYear('ALL'); setSelectedTerm('ALL'); }} />
                      </span>
                    )}
                    {selectedTerm !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                        Term: {selectedTerm}
                        <X size={12} className="cursor-pointer hover:text-blue-900" onClick={() => setSelectedTerm('ALL')} />
                      </span>
                    )}
                    {selectedDate && (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                        Date: {selectedDate}
                        <X size={12} className="cursor-pointer hover:text-emerald-900" onClick={() => setSelectedDate('')} />
                      </span>
                    )}
                    {selectedViolationFilter !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                        Type: {selectedViolationFilter}
                        <X size={12} className="cursor-pointer hover:text-red-900" onClick={() => setSelectedViolationFilter('ALL')} />
                      </span>
                    )}
                    {searchTerm && (
                      <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                        Query: "{searchTerm}"
                        <X size={12} className="cursor-pointer hover:text-slate-900" onClick={() => setSearchTerm('')} />
                      </span>
                    )}
                    <button onClick={clearAllFilters} className="text-[11px] text-slate-400 hover:text-slate-700 underline ml-auto cursor-pointer">
                      Clear filters
                    </button>
                  </div>
                )}
              </div>

              {/* Table Body */}
              <div className="overflow-x-auto max-h-[calc(100vh-320px)] min-h-[250px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="sticky top-0 z-10 bg-slate-50 border-b border-slate-100 text-[11px] uppercase font-bold text-slate-400 tracking-wider shadow-2xs">
                      <th className="py-3 px-4 bg-slate-50">Logged Time</th>
                      <th className="py-3 px-4 bg-slate-50">Violation Item</th>
                      <th className="py-3 px-4 bg-slate-50">Intervention</th>
                      <th className="py-3 px-4 bg-slate-50">Subject</th>
                      <th className="py-3 px-4 bg-slate-50">Logged By</th>
                      <th className="py-3 px-4 bg-slate-50">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-600">
                    {paginatedGroupedLogs.length > 0 ? (
                      paginatedGroupedLogs.map(([dateKey, logs]) => (
                        <React.Fragment key={dateKey}>
                          {/* Group Header */}
                          <tr className="bg-slate-100/70 border-y border-slate-200/60 font-mono">
                            <td colSpan={6} className="py-2 px-4 font-bold text-[11px] text-slate-700">
                              <div className="flex items-center gap-2">
                                <Calendar size={13} className="text-slate-500" />
                                <span>{dateKey}</span>
                                <span className="text-[10px] font-normal text-slate-400">
                                  ({logs.length} {logs.length === 1 ? 'entry' : 'entries'})
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* Records */}
                          {logs.map((log, index) => {
                            const item = renderSafeString(log.specific_item || log.violation_type) || "Unspecified";
                            const notes = renderSafeString(log.detailed_note || log.notes) || "—";
                            const subject = renderSafeString(log.subject_display_name || log.subject_name || log.subject?.name) || "—";
                            const loggedBy = renderSafeString(log.logged_by_name || log.logged_by?.full_name || log.employee_name) || "Staff";
                            const intervention = renderSafeString(log.intervention_applied || log.action_taken || log.intervention);

                            return (
                              <tr key={log.id || index} className="hover:bg-slate-50/60 transition-colors">
                                <td className="py-3.5 px-4 text-slate-500 whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    <Clock size={12} className="text-slate-400" />
                                    <span>{formatTime(log.time_of_infraction, log.date_of_infraction)}</span>
                                  </div>
                                </td>
                                <td className="py-3.5 px-4 font-semibold text-slate-900">{item}</td>
                                <td className="py-3.5 px-4">{getInterventionBadge(intervention)}</td>
                                <td className="py-3.5 px-4 text-slate-700">{subject}</td>
                                <td className="py-3.5 px-4 text-slate-700">{loggedBy}</td>
                                <td className="py-3.5 px-4 text-slate-500 max-w-xs truncate" title={typeof notes === 'string' ? notes : ''}>
                                  {notes}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-400 text-xs font-medium">
                          No infraction records found matching current query parameters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Group Pagination Controls */}
              {groupedLogs.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-slate-100 bg-slate-50/50 text-xs font-medium text-slate-500">
                  <div className="flex items-center gap-2">
                    <span>Groups per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs font-semibold text-slate-700 focus:outline-none cursor-pointer"
                    >
                      {[3, 5, 10, 20].map((size) => (
                        <option key={size} value={size}>
                          {size} groups
                        </option>
                      ))}
                    </select>
                    <span className="ml-2 text-slate-400">
                      Total {groupedLogs.length} date groups
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className="p-1.5 border border-slate-200 bg-white rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-white cursor-pointer"
                    >
                      <ChevronLeft size={14} />
                    </button>

                    <span className="px-3 py-1 font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg">
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className="p-1.5 border border-slate-200 bg-white rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-white cursor-pointer"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <NewViolationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveInfraction}
          studentId={studentId}
          classroomId={studentInfo?.classroomId}
          studentName={studentInfo?.name}
        />
      </div>
  );
}